import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { CatDescription, CatSex } from '@/types/models'
import type { Fix } from '@/services/location'

/**
 * Captures live on the device before they live anywhere else.
 *
 * Cats are met outdoors, often where there is no signal. The photograph, the
 * position and the moment are written to IndexedDB the instant the shutter
 * fires, and only then does anything try to reach the network. Nothing is ever
 * lost to a failed request. (§73)
 */

export type DraftStatus = 'pending' | 'syncing' | 'failed' | 'done'

export interface CaptureDraft {
  /** Also the sync_client_id, so replaying can never double-register. (§74) */
  id: string
  /**
   * De quién es. Sin esto, al cerrar sesión los borradores (foto y GPS exacto)
   * se quedaban en el móvil y la cuenta siguiente los subía como suyos. (§H19)
   * Opcional solo por los borradores de antes, que se adoptan al entrar.
   */
  ownerId?: string
  createdAt: number
  status: DraftStatus
  attempts: number
  lastError?: string

  photo: Blob
  thumbnail: Blob
  width: number
  height: number
  mimeType: string

  observedAt: string
  fix: Fix | null

  /** Set when the person recognised the cat before the draft was queued. */
  catId: string | null
  newCatName: string | null
  personalName: string | null
  sex: CatSex
  notes: string | null
  note: string | null
  city: string | null
  region: string | null
  country: string | null

  description: CatDescription | null
  embedding: number[] | null
  quality: number | null

  /** Filled in as the upload progresses, so a resumed sync skips finished steps. */
  sightingId?: string
  resolvedCatId?: string
  mediaId?: string
  uploaded?: boolean
}

interface CatGoDB extends DBSchema {
  drafts: {
    key: string
    value: CaptureDraft
    indexes: { 'by-status': DraftStatus; 'by-created': number }
  }
  meta: {
    key: string
    value: unknown
  }
}

let dbPromise: Promise<IDBPDatabase<CatGoDB>> | null = null

export function db(): Promise<IDBPDatabase<CatGoDB>> {
  if (!dbPromise) {
    dbPromise = openDB<CatGoDB>('catgo', 1, {
      upgrade(database) {
        const drafts = database.createObjectStore('drafts', { keyPath: 'id' })
        drafts.createIndex('by-status', 'status')
        drafts.createIndex('by-created', 'createdAt')
        database.createObjectStore('meta')
      },
    })
  }
  return dbPromise
}

export async function putDraft(draft: CaptureDraft): Promise<void> {
  const database = await db()
  await database.put('drafts', draft)
}

export async function patchDraft(id: string, patch: Partial<CaptureDraft>): Promise<void> {
  const database = await db()
  const existing = await database.get('drafts', id)
  if (!existing) return
  await database.put('drafts', { ...existing, ...patch })
}

export async function getDraft(id: string): Promise<CaptureDraft | undefined> {
  return (await db()).get('drafts', id)
}

export async function deleteDraft(id: string): Promise<void> {
  await (await db()).delete('drafts', id)
}

/** Los borradores de una cuenta, y solo de ella. Sin cuenta, ninguno. */
export async function listDrafts(ownerId: string | null): Promise<CaptureDraft[]> {
  if (!ownerId) return []
  const all = await (await db()).getAllFromIndex('drafts', 'by-created')
  return all.filter((d) => d.status !== 'done' && d.ownerId === ownerId).reverse()
}

export async function countPending(ownerId: string | null): Promise<number> {
  if (!ownerId) return 0
  const all = await (await db()).getAllFromIndex('drafts', 'by-status', 'pending')
  const failed = await (await db()).getAllFromIndex('drafts', 'by-status', 'failed')
  return [...all, ...failed].filter((d) => d.ownerId === ownerId).length
}

/**
 * Los borradores guardados antes de que llevaran dueño se adjudican a la
 * primera cuenta que entre. En un móvil de una sola persona —el caso normal—
 * son suyos; y dejarlos sin dueño los haría invisibles para siempre, que es
 * perderlos. Solo se hace una vez por borrador.
 */
export async function adoptOrphanDrafts(ownerId: string): Promise<number> {
  const store = (await db()).transaction('drafts', 'readwrite').store
  let adoptados = 0
  for (const d of await store.getAll()) {
    if (!d.ownerId) {
      await store.put({ ...d, ownerId })
      adoptados++
    }
  }
  return adoptados
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', value, key)
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db()).get('meta', key) as Promise<T | undefined>
}
